import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { IImageTemplate } from '../../../models/ImageTemplate';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { invalidateAdminImageTemplateBySystemTemplate } from './useAdminImageTemplateBySystemTemplateQuery';
import { ADMIN_IMAGE_TEMPLATE_UPDATE_MUTATION_KEY } from './useIsAdminImageTemplateUpdating';

interface IParams {
	file: File;
	sourceUrl: string;
	systemTemplateId?: string;
	templateId: string;
}

export function useAdminReplaceImageTemplateSourceMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (imageTemplate: IImageTemplate) => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation<IImageTemplate, Api.IOperationResultNoValue, IParams>({
		mutationFn: async ({ file, sourceUrl, templateId }) => {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('sourceUrl', sourceUrl);

			// FormData has to go through callWebServiceAsync, which yields the wrapped result rather than the value.
			const result = await userSession.webServiceHelper.callWebServiceAsync<IImageTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `admin/imageTemplates/${encodeURIComponent(templateId)}/source`,
				}),
				'PUT',
				formData
			);

			if (!result.success) {
				throw result;
			}

			return result.value;
		},
		mutationKey: ADMIN_IMAGE_TEMPLATE_UPDATE_MUTATION_KEY,
		onError: error =>
			onError ? onError(error) : toast.error(error?.systemMessage || 'The file could not be uploaded.'),
		onSettled: (_imageTemplate, _error, { systemTemplateId }) => {
			if (systemTemplateId) {
				invalidateAdminImageTemplateBySystemTemplate(systemTemplateId);
			}
		},
		onSuccess: imageTemplate => onSuccess?.(imageTemplate),
	});
}
